import { computed, createApp } from 'vue'
import { createPinia } from 'pinia'

import router from '@/router'

import store from './store'
import vuetify from '@/plugins/vuetify'
import * as Sentry from '@sentry/vue'
import 'vuetify/dist/vuetify.min.css'

import App from './App.vue'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import { setSpeckleConfig, useSpeckleStore, VueSpecklePlugin } from '@haskoning/vue-speckle'

import { useSentryFeedback } from '@/composables/useSentryFeedback'

const app = createApp(App)
const pinia = createPinia()
const { bus } = useEventsBus()

app.use(router).use(pinia).use(store).use(vuetify)

const speckleConfig = {
  serverUrl: import.meta.env.VITE_APP_SERVER_URL,
  appId: import.meta.env.VITE_APP_SPECKLE_ID,
  appSecret: import.meta.env.VITE_APP_SPECKLE_SECRET,
  appName: import.meta.env.VITE_APP_SPECKLE_NAME,
  workspaceId: import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID
}
setSpeckleConfig(
  speckleConfig.serverUrl,
  speckleConfig.workspaceId,
  speckleConfig.appId,
  speckleConfig.appSecret,
  speckleConfig.appName
)

app.use(VueSpecklePlugin, {
  componentsAutoSelectData: true,
  speckleStore: () => useSpeckleStore({ autoSelectData: false })
})

app.use(ComponentLibrary, { eventBus: bus })

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

router.isReady().then(() => {
  app.mount('#app')
  useSentryFeedback()
})

export default app
